import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateCorrection } from '@/features/memorization/hooks';
import { RUBRIC_SCORE_LABELS } from '@/features/memorization/labels';
import type { RubricScore } from '@/features/memorization/types';
import { ApiError } from '@/lib/api/client';

const RUBRIC_OPTIONS: readonly RubricScore[] = ['EXCELLENT', 'GOOD', 'FAIR', 'NEEDS_IMPROVEMENT'];

/**
 * `POST /memorization/targets/:id/corrections` (Phase G-2, Job 13) - a
 * standalone segment retracting a previously accepted range. `outcome` is
 * never sent from this form: the backend hardcodes a correction to
 * NEEDS_REVISION, so there is nothing to choose here.
 */
export function CreateCorrectionDialog({
  studentId,
  targetId,
  open,
  onOpenChange,
}: {
  readonly studentId: string;
  readonly targetId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [startAyah, setStartAyah] = useState('');
  const [endAyah, setEndAyah] = useState('');
  const [scoreAccuracy, setScoreAccuracy] = useState<RubricScore | ''>('');
  const [scoreFluency, setScoreFluency] = useState<RubricScore | ''>('');
  const [scoreTajwid, setScoreTajwid] = useState<RubricScore | ''>('');
  const [scoreConfidence, setScoreConfidence] = useState<RubricScore | ''>('');
  const [notes, setNotes] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const create = useCreateCorrection(studentId, targetId);

  function reset(): void {
    setStartAyah('');
    setEndAyah('');
    setScoreAccuracy('');
    setScoreFluency('');
    setScoreTajwid('');
    setScoreConfidence('');
    setNotes('');
    setValidationError(null);
    create.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    const startAyahNumber = Number(startAyah);
    const endAyahNumber = Number(endAyah);
    if (startAyah === '' || !Number.isInteger(startAyahNumber) || startAyahNumber < 1) {
      setValidationError('Masukkan ayat awal yang valid.');
      return;
    }
    if (endAyah === '' || !Number.isInteger(endAyahNumber) || endAyahNumber < 1) {
      setValidationError('Masukkan ayat akhir yang valid.');
      return;
    }
    if (endAyahNumber < startAyahNumber) {
      setValidationError('Ayat akhir tidak boleh lebih kecil dari ayat awal.');
      return;
    }
    if (
      scoreAccuracy === '' ||
      scoreFluency === '' ||
      scoreTajwid === '' ||
      scoreConfidence === ''
    ) {
      setValidationError('Lengkapi seluruh skor penilaian.');
      return;
    }

    create.mutate(
      {
        startAyah: startAyahNumber,
        endAyah: endAyahNumber,
        scoreAccuracy,
        scoreFluency,
        scoreTajwid,
        scoreConfidence,
        notes: notes.trim().length > 0 ? notes.trim() : null,
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
        },
      },
    );
  }

  const apiErrorMessage =
    create.error instanceof ApiError
      ? create.error.message
      : create.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Catat Koreksi</DialogTitle>
          <DialogDescription>
            Buka kembali rentang ayat yang sebelumnya diterima untuk perbaikan.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="correction-start-ayah">Ayat Awal</Label>
              <Input
                id="correction-start-ayah"
                type="number"
                min={1}
                value={startAyah}
                onChange={(event) => {
                  setStartAyah(event.target.value);
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="correction-end-ayah">Ayat Akhir</Label>
              <Input
                id="correction-end-ayah"
                type="number"
                min={1}
                value={endAyah}
                onChange={(event) => {
                  setEndAyah(event.target.value);
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {(
              [
                ['scoreAccuracy', 'Akurasi', scoreAccuracy, setScoreAccuracy],
                ['scoreFluency', 'Kelancaran', scoreFluency, setScoreFluency],
                ['scoreTajwid', 'Tajwid', scoreTajwid, setScoreTajwid],
                ['scoreConfidence', 'Kepercayaan Diri', scoreConfidence, setScoreConfidence],
              ] as const
            ).map(([field, label, value, setValue]) => (
              <div key={field} className="flex flex-col gap-2">
                <Label htmlFor={`correction-${field}`}>{label}</Label>
                <Select
                  value={value}
                  onValueChange={(next) => {
                    setValue(next as RubricScore);
                  }}
                >
                  <SelectTrigger id={`correction-${field}`}>
                    <SelectValue placeholder="Pilih" />
                  </SelectTrigger>
                  <SelectContent>
                    {RUBRIC_OPTIONS.map((score) => (
                      <SelectItem key={score} value={score}>
                        {RUBRIC_SCORE_LABELS[score]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="correction-notes">Catatan (opsional)</Label>
            <Textarea
              id="correction-notes"
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
              }}
            />
          </div>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Menyimpan...' : 'Simpan Koreksi'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
